import AnalyticsWebClient, {
	envType,
	originType,
	tenantType,
} from '@atlassiansox/analytics-web-client';

import type { ActiveThemeState } from '@atlaskit/tokens/theme-config';
import {
	EMBEDDED_CONFLUENCE_MODE,
	themeStateObjectToQueryString,
} from '@atlassian/embedded-confluence-common';

import type { PanelDataMap } from '../../common/constants';

export const onEditMode = (
	mode: EMBEDDED_CONFLUENCE_MODE,
	setPanelWidth: (width: number) => void,
): void | {} =>
	mode === EMBEDDED_CONFLUENCE_MODE.EDIT_MODE
		? setPanelWidth(Math.floor(window.innerWidth / 2))
		: {};

export const getAnalyticsEnv = (hostname: string): envType.LOCAL | envType.DEV | envType.PROD => {
	if (hostname.includes('localhost')) {
		return envType.LOCAL;
	}
	if (hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

export const appendThemeParam = (url: string, themeParam: string): string => {
	if (!themeParam) {
		return url;
	}

	if (!url.includes('?')) {
		return `${url}?${themeParam}`;
	}

	const [baseUrl, params] = url.split('?');

	const paramsArr = params.split('&');

	if (paramsArr.includes(themeParam)) {
		return url;
	}

	const otherParams = paramsArr
		.filter((param) => !(param.split('=')[0] === 'themeState'))
		.join('&');
	const newParams = otherParams ? `${themeParam}&${otherParams}` : themeParam;

	return `${baseUrl}?${newParams}`;
};

export const initializeAnalyticsClient = (cloudId?: string): AnalyticsWebClient => {
	const analyticsClient = new AnalyticsWebClient({
		product: 'confluencePreviewPanel',
		env: getAnalyticsEnv(window.location?.hostname),
		origin: originType.WEB,
	});

	if (cloudId) {
		analyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	}

	return analyticsClient;
};

export const resolveConfluencePreviewPanelUrl = (
	originalUrl: string,
	embedUrl?: PanelDataMap['embedUrl'],
): string => {
	if (!embedUrl) {
		return originalUrl;
	}

	try {
		const previewUrl = new URL(embedUrl);
		const contentId = previewUrl.searchParams.get('contentId');
		const spaceKey = previewUrl.searchParams.get('spaceKey');
		const hostname = previewUrl.searchParams.get('hostname');

		if (contentId && spaceKey && hostname) {
			const decodedSpaceKey = decodeURIComponent(spaceKey);
			const resolved = new URL(
				`https://${hostname}/wiki/spaces/${decodedSpaceKey}/pages/${contentId}`,
			);

			// Preserve all original URL parameters
			const originalUrl_ = new URL(originalUrl);
			for (const [key, value] of originalUrl_.searchParams) {
				resolved.searchParams.set(key, value);
			}

			return resolved.toString();
		}
	} catch {
		// If URL parsing fails, fall back to original URL
		return originalUrl;
	}

	return originalUrl;
};

export const constructUrl = (
	themeState: Partial<ActiveThemeState>,
	url: string,
	panelData?: PanelDataMap,
): string => {
	const themeStateParam = themeState
		? `themeState=${themeStateObjectToQueryString(themeState)}`
		: '';
	const cardStateUrl = panelData ? resolveConfluencePreviewPanelUrl(url, panelData?.embedUrl) : url;
	const formattedUrl = themeStateParam
		? appendThemeParam(cardStateUrl, themeStateParam)
		: cardStateUrl;
	return formattedUrl;
};
